import {AfterContentInit, AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {User} from '@core/shared/models/user';
import {UserService} from '@core/shared/services/user.service';
import {TranslateService} from '@ngx-translate/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ActivatedRoute} from '@angular/router';
import {FormService} from '@core/shared/services/form.service';
import {AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {isRequired} from "@core/shared/models/society/required-region-country";
import {BehaviorSubject, Observable} from "rxjs";
import {Country} from "@core/shared/models/country";
import {tap} from "rxjs/operators";
import {CountryService} from "@core/shared/services/country.service";
import {TranslationService} from "@core/shared/services/translation.service";
import {LocationField, LocationFieldType} from "@core/shared/models/location";
import {InputDateConfig} from "@lib/form/fields/input-date/input-date.component";
import {DATE_FORMAT} from "@app/data";
import {ServiceService} from "@core/shared/services/service.service";
import {MatTab, MatTabGroup} from "@angular/material/tabs";
import {SocietyDocumentType} from "@core/shared/models/society";
import {Recipient, RecipientStatus} from "@core/shared/models/recipient";
import {Currency} from "@core/shared/models/currency";
import {CurrencyService} from "@core/shared/services/currency.service";
import {SocietyValidationAccountDialogData, SocietyValidationAccountOrigin} from "@core/shared/models/society-validation-account";
import {ScaValidationRequiredDialogComponent} from "@core/components/sca/sca-validation-required-dialog/sca-validation-required-dialog.component";
import {ScaService} from "@core/shared/services/sca.service";
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {IdentityVerification} from '@core/shared/models/identity-verification';
import {ConfirmDialogComponent} from '@lib/confirm-dialog/confirm-dialog.component';

@Component({
    selector: 'app-page-service-update',
    templateUrl: './page-service-list.component.html',
    styleUrls: ['./page-service-list.component.scss'],
    providers: [
        FormService
    ],
    standalone: false
})
export class PageServiceListComponent implements OnInit, AfterViewInit, AfterContentInit {

    @ViewChild('tabGroup', {static: true}) tabGroup: MatTabGroup;

    @ViewChild('documentTab', {static: false}) documentTabRef: MatTab;

    public user: BehaviorSubject<User> = new BehaviorSubject(null);

    public selectedIndex: number;

    public countries$: Observable<Country[]>;

    public requiredVatNumberCountryCodes: string[] = [];

    public requiredDocuments: SocietyDocumentType[] = [];

    public currencies: Currency[] = [];

    public recipient: Recipient = null;

    public identityVerification: IdentityVerification = null;

    constructor(
        private _dialog: MatDialog,
        private _changeDetectorRef: ChangeDetectorRef,
        private _countryService: CountryService,
        private _userService: UserService,
        private _translateService: TranslateService,
        private _snackBar: MatSnackBar,
        private _activatedRoute: ActivatedRoute,
        public formService: FormService,
        private _formBuilder: UntypedFormBuilder,
        private _scaService: ScaService,
        public translationService: TranslationService,
        private _currencyService: CurrencyService,
        public _serviceService: ServiceService
    ) {}

    ngOnInit(): void {

        this._activatedRoute.data.subscribe((data: { user: User }): void => {

            this.user.next(data.user);

            if(this.documentsManagementAllowed) {

                this._loadRequiredDocuments();
            }

            this._loadCountries();

            this._loadCurrencies();

            this._initForm();

            this._loadServices();

            this.form.patchValue(data.user);

            this.societyLegalRepresentativeControl.disable();
        });
    }

    ngAfterViewInit() {

        if('targetTab' in this._activatedRoute.snapshot.queryParams){

            switch (this._activatedRoute.snapshot.queryParams['targetTab']){

                case 'document':

                    this.tabGroup.selectedIndex = this.documentTabRef.position;

                    break;
            }
        }

    }

    ngAfterContentInit() {

        this._changeDetectorRef.detectChanges();
    }

    private _loadRequiredDocuments(): void {

        this.requiredDocuments = this.user.value.society.requiredMangoPayDocuments;
    }

    private _loadCountries(): void {

        this.countries$ = this._countryService.getItemsAPI().pipe(tap((countries: Country[]): void => {

            this.requiredVatNumberCountryCodes = countries.filter((country: Country): boolean => {

                return country.requireSocietyVatNumber;

            }).map((country: Country): string => {

                return country.code;
            });
        }));
    }

    private _loadCurrencies(): void {

        this._currencyService.getItemsAPI().subscribe((currencies: Currency[]): void => {

            this.currencies = currencies;

            if(this.isRequiredDocument('recipient')){

                this.recipientBankTransferControl.get('currency').patchValue(this.currencies.find((currency: Currency) => currency.code === 'EUR'));
            }
        });
    }

    private _loadServices(): void {

        if(this.isRequiredDocument('recipient')) {

            this._serviceService.getRecipientItemAPI(this.user.value.society.id).subscribe((recipient: Recipient): void => {

                this.recipient = recipient;

                this.recipientControl.patchValue(recipient);
            });
        }

        if(this.isRequiredDocument('identity-verification')) {

            this._serviceService.getIdentityVerificationItemAPI(this.user.value.society.id).subscribe((identityVerification: IdentityVerification): void => {

                this.identityVerification = identityVerification;
            });
        }
    }

    private _initForm(): void {

        this.formService.form = this._formBuilder.group({
            society: this._formBuilder.group({
                legalRepresentative: this._formBuilder.group({
                    id: [null],
                    civility: [null, [Validators.required]],
                    firstName: ['', [Validators.required]],
                    lastName: ['', [Validators.required]],
                    birthDay: ['', [Validators.required]],
                    nationality: ['', [Validators.required]],
                    phone: [{ value: '', disabled: true }],
                    address: ['', [Validators.required]],
                    additionalAddress: [''],
                    zipcode: ['', [Validators.required]],
                    city: ['', [Validators.required]],
                    region: ['', [(control: UntypedFormControl) => {

                        if (!control.parent || !control.parent.get('country').value.length)
                            return;

                        const country: string = control.parent.get('country').value;

                        return (!control.value || !control.value.length) && isRequired(country) ? {isRequired: {valid: false}} : null;
                    }]],
                    country: ['', [Validators.required]]
                }),
                recipient: this._formBuilder.group({
                    ownerName: ['', [Validators.required]],
                    address: this._formBuilder.group({
                        address: ['', [Validators.required]],
                        additionalAddress: [''],
                        city: ['', [Validators.required]],
                        region: ['', [Validators.required]],
                        zipcode: ['', [Validators.required]],
                        country: ['', [Validators.required]],
                    }),
                    bankTransfer: this._formBuilder.group({
                        iban: ['', [Validators.required]],
                        bic: ['', [Validators.required]],
                        currency: [{ value: null, disabled: true }, [Validators.required]],
                    })
                })
            })
        });

    }

    private _openScaValidationRequiredDialog(redirectUrl: string, locale: string): void {

        const data: SocietyValidationAccountDialogData = {
            origin: SocietyValidationAccountOrigin.RecipientUpdate,
            redirectUrl: redirectUrl,
            returnUrl: `${this._scaService.getValidationResponseUrl(locale)}?lang=${this.localeId}&origin=${SocietyValidationAccountOrigin.RecipientUpdate}`
        };

        this._dialog.open(ScaValidationRequiredDialogComponent, {
            disableClose: true,
            width: '800px',
            data: data
        });
    }

    public accountSubmit(): void {

        if (this.recipientControl.invalid) {

            this.formService.showValidationMessages(this.recipientControl);

            return;
        }

        const data: Partial<Recipient> = {...this.recipientControl.getRawValue()};

        this._serviceService.createRecipientItemAPI(data, this.user.value.society.id).subscribe((recipient: Recipient): void => {

            this._snackBar.open(this._translateService.instant('services.form.file.bank_account.success.value'), this._translateService.instant('notification.close.action.value'), {
                duration: 5000
            });

            if(recipient.validationAccount.isValid){

                this._loadServices();
            }
            else {

                this._openScaValidationRequiredDialog(recipient.validationAccount.redirectUrl, this.localeId);
            }
        });
    }

    public getAddressLocationFields(form: AbstractControl): LocationField[] {

        return [
            {
                type: LocationFieldType.Street,
                reference: form.get('address')
            },
            {
                type: LocationFieldType.Postcode,
                reference: form.get('zipcode')
            },
            {
                type: LocationFieldType.City,
                reference: form.get('city')
            },
            {
                type: LocationFieldType.Region,
                reference: form.get('region')
            },
            {
                type: LocationFieldType.CountryISO,
                reference: form.get('country')
            }
        ];
    }

    public isRefused(status: string): boolean {

        return status === 'refused';
    }

    public isRequiredDocument(type: SocietyDocumentType): boolean {

        return this.requiredDocuments.some((item: SocietyDocumentType): boolean => {

            return type === item;
        });
    }

    public isAccountAdmin(): boolean {

        return !!this.user.value.societyAdmin;
    }

    public compareCurrency(a: Currency, b: Currency): boolean {

        if(!a || !b){

            return false;
        }

        return a.code === b.code;
    }

    public openIdentityVerificationDialog(): void {

        const title : string = this._translateService.instant('services.form.identityVerification.tunnel.title.value');

        const content : string = this._translateService.instant('services.form.identityVerification.tunnel.content.value');

        const dialogRef: MatDialogRef<ConfirmDialogComponent> = this._dialog.open(ConfirmDialogComponent, {
            width: '500px',
            data: {
                title: title,
                content: content
            }
        });

        dialogRef.componentInstance.confirm.subscribe((): void => {

            this._serviceService.createIdentityVerificationItemAPI(this.user.value.society.id).subscribe((data: IdentityVerification): void => {

                dialogRef.close();
                window.open(data.validationAccount.redirectUrl, '_blank');
            });
        });
    }

    get localeId(): string {

        return this._translateService.currentLang;
    }

    get form(): UntypedFormGroup {

        return this.formService.form;
    }

    get societyForm(): UntypedFormGroup {

        return this.form.get('society') as UntypedFormGroup;
    }

    get societyLegalRepresentativeControl(): UntypedFormGroup {

        return this.societyForm.get('legalRepresentative') as UntypedFormGroup;
    }

    get recipientControl(): UntypedFormGroup {

        return this.societyForm.get('recipient') as UntypedFormGroup;
    }

    get recipientAddressControl(): UntypedFormGroup {

        if(!this.recipientControl) {

            return null;
        }

        return this.recipientControl.get('address') as UntypedFormGroup;
    }

    get recipientBankTransferControl(): UntypedFormGroup {

        if(!this.recipientControl) {

            return null;
        }

        return this.recipientControl.get('bankTransfer') as UntypedFormGroup;
    }

    get birthDayConfig(): InputDateConfig {

        return {
            id: 'birthDay',
            enableReset: false,
            attrs: {
                required: true,
                placeholder: this._translateService.instant('form.user.fields.birthDay.value'),
                disabled: true
            },
            format: DATE_FORMAT
        };
    }

    get documentsManagementAllowed(): boolean {

        if(this._userService.hasOneOfThisRoles(this._userService.currentUser.getValue(), ['ROLE_PROVIDER', 'ROLE_INSTITUTIONAL', 'ROLE_FEDERATION'])){

            return false;
        }

        if(!this.isAccountAdmin()){

            return false;
        }

        return this._userService.hasAccess(this._userService.currentUser.getValue(), 'ONLINE_SALE');
    }

    get recipientStatusLabel(): string {

        if(!this.recipient){

            return null;
        }

        switch(this.recipient.status){

            case RecipientStatus.ACTIVE:

                return 'recipient.status.active.label.value';

            case RecipientStatus.WAITING:

                return 'recipient.status.waiting.label.value';

            case RecipientStatus.CANCELED:

                return 'recipient.status.canceled.label.value';

            case RecipientStatus.DEACTIVATED:

                return 'recipient.status.deactivated.label.value';

            default:

                return null;
        }
    }
}
